/* werk Showroom – motion for the demo store's page frame (layout theme.showroom, sections showroom-*).
   Cover intro + parallax, sticky bar with the active look (steps aside for a section's own floating UI), reveals on scroll, smooth jumps to the chapters.
   Vanilla, no libraries. Horizon may scroll div.page-wrapper instead of the window, so scroll is listened to in the
   capture phase on document (catches any scroller) and positions are read with getBoundingClientRect. */
(() => {
  if (window.werkShowroom) return;
  window.werkShowroom = true;

  const root = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  // phones/tablets: no scroll parallax – touch scrolling runs off the main thread, a JS-driven layer always lags behind it
  const still = matchMedia('(max-width: 989px), (pointer: coarse)');
  const isStatic = () => root.classList.contains('sr-static');

  /* ---- reveals: once, then unobserve ---- */
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
  );

  /* ---- lead sentence: one span per word; the full text stays readable for screen readers ---- */
  function splitWords(el) {
    if (el.dataset.srSplit) return;
    el.dataset.srSplit = '1';
    const text = el.textContent.trim().replace(/\s+/g, ' ');
    const sr = document.createElement('span');
    sr.className = 'sr-sr';
    sr.textContent = text;
    const words = document.createElement('span');
    words.setAttribute('aria-hidden', 'true');
    text.split(' ').forEach((w, i) => {
      const s = document.createElement('span');
      s.className = 'sr-word';
      s.style.setProperty('--i', i);
      s.textContent = w;
      words.append(s, i < text.split(' ').length - 1 ? ' ' : '');
    });
    el.replaceChildren(sr, words);
  }

  /* ---- scroll loop: only while something needs it ---- */
  const onFrame = new Set();
  let ticking = false;
  function requestTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      for (const fn of onFrame) fn();
    });
  }
  document.addEventListener('scroll', requestTick, { capture: true, passive: true });
  addEventListener('resize', requestTick, { passive: true });

  /* ---- cover: intro once the image is decoded (max 1.2 s), parallax while visible ---- */
  function initCover(cover) {
    if (cover.dataset.srReady) return;
    cover.dataset.srReady = '1';
    const img = cover.querySelector('.sr-cover_img');
    const show = () => requestAnimationFrame(() => cover.classList.add('is-ready'));
    // a page-load intro (Burst Loader) covers the page first – the cover opens once it is gone
    const ready = () => {
      const intro = () => document.querySelector('werk-burst-loader.is-on');
      if (!intro()) return show();
      const mo = new MutationObserver(() => { if (!intro()) { mo.disconnect(); show(); } });
      mo.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });
      setTimeout(() => { mo.disconnect(); show(); }, 9000);
    };
    if (!img || img.complete || isStatic()) ready();
    else {
      const loaded = img.decode ? img.decode().catch(() => {}) : new Promise((r) => img.addEventListener('load', r, { once: true }));
      Promise.race([loaded, new Promise((r) => setTimeout(r, 1200))]).then(ready);
    }

    let visible = true;
    let last = -1;
    const update = () => {
      if (!cover.isConnected) return onFrame.delete(update);
      if (!visible || reduce.matches) return;
      if (still.matches) {
        if (last !== -1) { cover.style.removeProperty('--sr-y'); cover.style.removeProperty('--sr-p'); last = -1; }
        return;
      }
      const r = cover.getBoundingClientRect();
      const y = Math.max(0, -r.top);
      if (y === last) return;
      last = y;
      cover.style.setProperty('--sr-y', `${(y * 0.38).toFixed(1)}px`);
      cover.style.setProperty('--sr-p', Math.min(1, y / r.height).toFixed(3));
    };
    onFrame.add(update);
    new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible) requestTick();
    }).observe(cover);
  }

  /* ---- floating UI of the werk sections themselves. The section always wins: while one of these is on screen and
     sits where the bar sits, the bar steps aside and comes back afterwards. New sections with fixed/sticky parts
     get a line here. ---- */
  const FLOATING = [
    'werk-product-comparison.is-stuck .werk-product-comparison_sticky-pill', // switcher pill, phones, top
    '.werk-product-buy-box_bar.is-on', // buy bar, bottom
    '.werk-product-showcase_bar.is-on', // buy bar, phones, bottom
    '.werk-build-a-box_tray', // box tray, phones, sticky bottom
    '.werk-product-canvas_dock:not([hidden])', // canvas dock, bottom of the full-screen canvas
    '.werk-product-canvas_inner.is-open .werk-product-canvas_panel', // product panel / bottom sheet
    '.werk-build-a-box_sheet[open]',
    'werk-hotspot-panel.is-open .werk-hotspot-panel_panel', // card / bottom sheet
    'werk-hotspot-popover.is-open .werk-hotspot-popover_popover',
    'werk-hotspot-index.is-open .werk-hotspot-index_index',
    'werk-hotspot-portal.is-open .werk-hotspot-portal_portal',
    '[class^="werk-"]:popover-open', // anything else a werk section lifts into the top layer
    'dialog[open]',
    '.sr-consent.is-open [data-sr-float]', // the showroom's own cookie card (phones: full width at the bottom)
    '.werk-magnetic-nav_panel:not([hidden])', // nav pages: open menu / search panel
    '.werk-magnetic-mega-nav_panel:not([hidden])',
    '.werk-magnetic-nav_corner', // logo in the corner (top)
    '.werk-magnetic-mega-nav_corner',
  ].join(',');
  // navigation pages: the nav's dock – the bar takes the other end of the screen
  const DOCKS = '.werk-magnetic-nav_bar, .werk-magnetic-mega-nav_bar';

  /* ---- sticky bar: in once the cover is gone, out while a section's own floating UI needs its spot;
     marks the look currently on screen ---- */
  function initBar() {
    const bar = document.querySelector('[data-sr-bar]');
    if (!bar || bar.dataset.srReady) return;
    bar.dataset.srReady = '1';
    const cover = document.querySelector('[data-sr-cover]');
    const phone = matchMedia('(max-width: 749px)');
    const docked = bar.classList.contains('sr-bar--dock'); // phones only (CSS), across from the nav's dock
    let past = !cover;
    let yielding = false;
    let atTop = false;
    const apply = () => {
      const on = past && !yielding;
      bar.classList.toggle('is-on', on);
      bar.classList.toggle('is-yield', past && yielding);
      bar.inert = !on;
      bar.setAttribute('aria-hidden', String(!on));
    };
    if (cover)
      new IntersectionObserver(([e]) => {
        past = !e.isIntersecting && e.boundingClientRect.top < 0;
        apply();
      }).observe(cover);
    apply();

    // where the bar sits when it is in (it is off-screen while out, so its rect can't be read)
    const spot = () => {
      const h = bar.offsetHeight;
      const x = bar.getBoundingClientRect(); // only moves up/down in and out, so left/right are where it sits
      const pad = 10;
      const top = phone.matches && !atTop ? innerHeight - 14 - h : 14;
      return { top: top - pad, bottom: top + h + pad, left: x.left - pad, right: x.right + pad };
    };
    const shown = (el) => {
      const r = el.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return null;
      const cs = getComputedStyle(el);
      return cs.visibility === 'hidden' || cs.display === 'none' || +cs.opacity < 0.05 ? null : r;
    };
    const checkYield = () => {
      if (!bar.isConnected) return onFrame.delete(checkYield);
      if (docked) {
        // dock in the lower half → bar at the top, otherwise at the bottom (its phone default); dock out of sight → stay
        const d = [...document.querySelectorAll(DOCKS)].map(shown).find(Boolean);
        const top = d ? d.top + d.height / 2 > innerHeight / 2 : atTop;
        if (top !== atTop) {
          // switching ends: jump, don't fly across the screen
          bar.style.transition = 'none';
          bar.classList.toggle('is-top', (atTop = top));
          void bar.offsetWidth;
          bar.style.transition = '';
        }
      }
      const b = spot();
      let y = false;
      for (const el of document.querySelectorAll(FLOATING)) {
        if (el.closest('.sr') && !el.hasAttribute('data-sr-float')) continue;
        const r = shown(el);
        if (r && r.bottom > b.top && r.top < b.bottom && r.right > b.left && r.left < b.right) {
          y = true;
          break;
        }
      }
      if (y === yielding) return;
      yielding = y;
      apply();
    };
    onFrame.add(checkYield);
    checkYield();
    // sections open/close their floating parts with classes, [open] and popovers – re-check on any of those
    new MutationObserver(requestTick).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class', 'open', 'hidden'] });
    document.addEventListener('toggle', requestTick, { capture: true });
    document.addEventListener('transitionend', requestTick, { capture: true, passive: true });

    const links = [...bar.querySelectorAll('[data-sr-chapter-link]')];
    if (!links.length) return;
    let current = '';
    const update = () => {
      if (!bar.isConnected) return onFrame.delete(update);
      // the active look is the last chapter whose head has passed 40 % of the viewport
      const line = innerHeight * 0.4;
      let id = links[0].hash.slice(1);
      for (const a of links) {
        const t = document.getElementById(a.hash.slice(1));
        if (t && t.getBoundingClientRect().top < line) id = t.id;
      }
      if (id === current) return;
      current = id;
      links.forEach((a) => {
        const on = a.hash.slice(1) === id;
        a.classList.toggle('is-active', on);
        if (on) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
        // a very narrow phone scrolls the bar's looks: keep the active one in view
        const strip = on && a.closest('.sr-bar_looks');
        if (strip && strip.scrollWidth > strip.clientWidth) {
          strip.scrollTo({ left: a.offsetLeft - strip.offsetLeft - (strip.clientWidth - a.offsetWidth) / 2, behavior: 'smooth' });
        }
      });
    };
    onFrame.add(update);
    update();
  }

  /* ---- jumps to #classic / #drawer … glide instead of snapping ---- */
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest('.sr a[href^="#"]');
    if (!a || a.hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(a.hash.slice(1)));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', a.hash);
  });

  /* ---- section clips in "More like this": load when near, play while visible, pause otherwise.
     Reduced motion: poster only. iOS low-power mode can refuse play() – the poster stays then. ---- */
  const clipIO = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const v = e.target;
        if (e.isIntersecting) {
          if (!v.src) { v.src = v.dataset.srClip; v.load(); }
          if (!reduce.matches) v.play().catch(() => {});
        } else if (!v.paused) v.pause();
      }
    },
    { rootMargin: '200px 0px', threshold: 0.01 }
  );
  document.addEventListener('visibilitychange', () => {
    // hidden: pause all; back: observe again so the visible ones get their entry (and play) once more
    document.querySelectorAll('[data-sr-clip]').forEach((v) => {
      if (document.hidden) v.pause();
      else { clipIO.unobserve(v); clipIO.observe(v); }
    });
  });

  /* ---- buttons: the hover of werk.build (Osmo "Button 036"; same markup as site/src/scripts/button-036.ts, styles
     in showroom.css). The button keeps its tag, classes, attributes and listeners; its content moves into the
     default label and a copy becomes the hover label (aria-hidden, without ids / data-*, so scripts only ever find
     the real one). A label a script changes later (the bag count) is copied over. ---- */
  const B036 = '.sr-btn, .sr-back, .sr-bagpill';
  const span036 = (cls) => Object.assign(document.createElement('span'), { className: cls });
  function strip036(el) {
    el.removeAttribute('id');
    for (const a of [...el.attributes]) if (a.name.startsWith('data-') || a.name === 'aria-live') el.removeAttribute(a.name);
    [...el.children].forEach(strip036);
  }
  function copy036(def, hover) {
    const twin = def.cloneNode(true);
    [...twin.children].forEach(strip036);
    hover.replaceChildren(...twin.childNodes);
  }
  function enhance036(btn) {
    if (btn.classList.contains('button-036')) return;
    const bg = span036('button-036__bg');
    for (let i = 0; i < 5; i++) {
      const s = span036('button-036__bg-span');
      s.style.setProperty('--index', i);
      bg.append(s);
    }
    const inner = span036('button-036__inner');
    const def = span036('button-036__text is--default');
    def.append(...btn.childNodes);
    const hover = span036('button-036__text is--hover');
    hover.setAttribute('aria-hidden', 'true');
    inner.append(def, hover);
    btn.append(bg, inner);
    btn.classList.add('button-036');
    copy036(def, hover);
    let queued = false;
    new MutationObserver((records) => {
      if (queued || !records.some((r) => def.contains(r.target))) return; // our own copy / the stripes
      queued = true;
      queueMicrotask(() => {
        queued = false;
        copy036(def, hover);
      });
    }).observe(btn, { childList: true, subtree: true, characterData: true });
  }
  const buttons036 = (scope) => scope.querySelectorAll(B036).forEach(enhance036);

  /* ---- note at the edge of the screen (one at a time) ---- */
  let toastEl = null;
  let toastTimer = 0;
  function toast(text) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'sr sr-toast';
      toastEl.setAttribute('role', 'status');
      // top layer where it exists, so the note also shows over a section's open dialog or sheet
      if ('popover' in toastEl) toastEl.popover = 'manual';
      document.body.append(toastEl);
    }
    toastEl.textContent = text;
    if (toastEl.popover) {
      // re-show: moves it above anything that entered the top layer since
      if (toastEl.matches(':popover-open')) toastEl.hidePopover();
      toastEl.showPopover();
    }
    void toastEl.offsetWidth; // start state painted before the class flips
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 2600);
  }

  /* ---- no product or collection pages in the showroom (User, 10.10.2026): links to them stay where the section
     draws them – it is a demo of the section as it is – but they no longer leave the page. Listens on window in
     the bubble phase, so a section that handles the click itself (quick add, a panel that opens) goes first and
     is left alone. Addresses typed by hand end on the "Props only" page (templates/product.json, collection.json). ---- */
  const OFF_STAGE = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?(products|collections)(?:\/|$)/i;
  function offStage(e) {
    if (e.defaultPrevented || isStatic() || (e.type === 'auxclick' && e.button !== 1)) return; // middle click opens a tab – block that too
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a || a.closest('.sr')) return;
    let url;
    try {
      url = new URL(a.getAttribute('href'), location.href);
    } catch {
      return;
    }
    if (url.origin !== location.origin) return;
    const m = url.pathname.match(OFF_STAGE);
    if (!m) return;
    e.preventDefault();
    toast(`${m[1].toLowerCase() === 'products' ? 'Product' : 'Collection'} pages are switched off in the showroom.`);
  }
  addEventListener('click', offStage);
  addEventListener('auxclick', offStage);

  /* ---- bag: the showroom's own cart drawer (snippets/showroom-bag.liquid). Rendered from the cart JSON, lines
     without links, no checkout. Opens from the bag pill, from the Magnetic Nav's cart button (it calls open() on
     <cart-drawer>) and by itself after a section has added something. ---- */
  function initBag() {
    const dlg = document.querySelector('[data-sr-bag]');
    if (!dlg || dlg.dataset.srReady) return;
    dlg.dataset.srReady = '1';
    const body = dlg.querySelector('[data-sr-bag-body]');
    const itemsLabel = dlg.querySelector('[data-sr-bag-items]');
    const base = () => window.Shopify?.routes?.root || '/';
    const phone = matchMedia('(max-width: 749px)');
    let cart = null;
    let own = false; // true while this bag announces its own change
    try {
      cart = JSON.parse(dlg.querySelector('[data-sr-bag-data]').textContent);
    } catch {}

    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    const money = (cents) => {
      const digits = cents % 100 ? 2 : 0;
      try {
        return new Intl.NumberFormat(root.lang || 'en', { style: 'currency', currency: cart?.currency || 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(cents / 100);
      } catch {
        return (cents / 100).toFixed(digits);
      }
    };
    const thumb = (src) => {
      try {
        const u = new URL(src, location.href);
        u.searchParams.set('width', '240');
        return u.href;
      } catch {
        return '';
      }
    };
    // where the button leads: the code of the section whose page this is, else the catalogue
    const way = () => {
      const cover = document.querySelector('[data-sr-cover][data-sr-name]');
      const back = cover?.querySelector('[data-sr-back]');
      return cover && back ? { label: `Get the code for ${cover.dataset.srName}`, href: back.href } : { label: 'Browse all sections on werk.build', href: 'https://werk.build' };
    };
    const arrow = '<svg class="sr-btn_arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4.5 11.5 11.5 4.5m0 0H5.6m5.9 0v5.9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    function render() {
      const n = cart?.item_count || 0;
      itemsLabel.textContent = `${n} ${n === 1 ? 'item' : 'items'}`;
      document.querySelectorAll('[data-sr-bag-count]').forEach((el) => (el.textContent = n));
      document.querySelectorAll('[data-sr-bag-open]').forEach((el) => (el.dataset.count = n));
      // keep the keyboard where it was: the same control of the same line gets the focus back
      const f = dlg.contains(document.activeElement) ? document.activeElement : null;
      const at = f?.dataset.act ? [f.closest('[data-key]')?.dataset.key || '', f.dataset.act] : null;
      if (!n) {
        body.innerHTML =
          '<div class="sr-bag_empty"><p class="sr-mono">Nothing in here</p><p class="sr-bag_empty-title">Your bag is empty.</p>' +
          '<p class="sr-bag_empty-text">Add something in any demo. The bag is only here so you can watch add to cart, counters and drawers do their job.</p>' +
          '<button class="sr-btn sr-btn--tint" type="button" data-sr-bag-close>Keep looking</button></div>';
      } else {
        const lines = cart.items
          .map((it) => {
            const variant = it.product_has_only_default_variant ? '' : it.variant_title || '';
            return (
              `<li class="sr-bag_line" data-key="${esc(it.key)}" data-qty="${it.quantity}">` +
              `<span class="sr-bag_thumb">${it.image ? `<img src="${esc(thumb(it.image))}" width="80" height="100" loading="lazy" alt="">` : ''}</span>` +
              `<div class="sr-bag_text"><p class="sr-bag_name">${esc(it.product_title)}</p>${variant ? `<p class="sr-mono sr-bag_variant">${esc(variant)}</p>` : ''}` +
              `<div class="sr-bag_qty"><button type="button" data-act="dec" aria-label="One less">–</button><span class="sr-mono" aria-label="Quantity">${it.quantity}</span><button type="button" data-act="inc" aria-label="One more">+</button></div></div>` +
              `<div class="sr-bag_side"><p class="sr-bag_price">${esc(money(it.final_line_price))}</p><button class="sr-bag_link sr-bag_remove" type="button" data-act="remove">Remove</button></div>` +
              '</li>'
            );
          })
          .join('');
        const to = way();
        body.innerHTML =
          '<p class="sr-bag_note">Demo store – nothing ships, nothing is charged.</p>' +
          `<ul class="sr-bag_lines">${lines}</ul>` +
          `<div class="sr-bag_foot"><p class="sr-bag_sub"><span>Subtotal</span><b>${esc(money(cart.total_price))}</b></p>` +
          '<p class="sr-mono sr-bag_fine">Checkout is switched off in the showroom.</p>' +
          `<a class="sr-btn sr-btn--ink" href="${esc(to.href)}" target="werk-site"><span>${esc(to.label)}</span>${arrow}</a>` +
          '<button class="sr-bag_link sr-bag_clear" type="button" data-act="clear">Empty bag</button></div>';
      }
      buttons036(body);
      if (at) {
        const line = at[0] ? [...body.querySelectorAll('[data-key]')].find((li) => li.dataset.key === at[0]) : body;
        (line?.querySelector(`[data-act="${at[1]}"]`) || dlg.querySelector('.sr-bag_panel'))?.focus();
      }
    }

    // other parts of the page that show the cart (the Magnetic Nav's counter) hear about a change made in here
    const announce = () => {
      own = true;
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      own = false;
    };
    async function refresh() {
      try {
        cart = await fetch(base() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        render();
      } catch {}
    }
    async function post(path, data) {
      dlg.classList.add('is-busy');
      try {
        const res = await fetch(base() + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data || {}) });
        const json = await res.json();
        if (res.ok && json.items) {
          cart = json;
          render();
        } else await refresh(); // e.g. more than is in stock: show what the cart really holds
      } catch {
        await refresh();
      }
      dlg.classList.remove('is-busy');
      announce();
    }

    const open = () => {
      if (dlg.open) return;
      dlg.style.removeProperty('translate');
      dlg.style.removeProperty('transition');
      dlg.showModal();
    };
    const close = () => dlg.open && dlg.close();
    const host = dlg.closest('cart-drawer');
    if (host) {
      host.open = open;
      host.close = close;
    }

    document.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t.closest('[data-sr-bag-open]')) {
        e.preventDefault();
        return open();
      }
      if (!dlg.contains(t)) return;
      if (t === dlg || t.closest('[data-sr-bag-close]')) return close(); // the backdrop counts as the dialog itself
      const btn = t.closest('[data-act]');
      if (!btn) return;
      const line = btn.closest('[data-key]');
      const qty = +(line?.dataset.qty || 0);
      if (btn.dataset.act === 'clear') post('cart/clear.js');
      else if (line) post('cart/change.js', { id: line.dataset.key, quantity: btn.dataset.act === 'inc' ? qty + 1 : btn.dataset.act === 'dec' ? qty - 1 : 0 });
    });

    // the werk sections announce an add with shopify:cart:lines-update (plus cart:refresh): one fetch for all of
    // them, then the bag opens – a beat later, so the section's own "Added" is seen first
    let pending = 0;
    let wantOpen = false;
    const heard = (e) => {
      if (own) return;
      if (e.type === 'shopify:cart:lines-update') wantOpen = true;
      clearTimeout(pending);
      pending = setTimeout(async () => {
        await refresh();
        if (wantOpen && cart?.item_count) setTimeout(open, 500);
        wantOpen = false;
      }, 60);
    };
    ['shopify:cart:lines-update', 'cart:refresh', 'cart:update', 'cart:updated'].forEach((n) => document.addEventListener(n, heard));
    // back from the bfcache or another tab: the cart may have changed there
    addEventListener('pageshow', (e) => e.persisted && refresh());

    // phones: pull the sheet down by its head to close it
    let pull = null;
    dlg.addEventListener('pointerdown', (e) => {
      if (!phone.matches || !e.target.closest('[data-sr-bag-grab]') || e.target.closest('button')) return;
      pull = { y: e.clientY, t: performance.now(), dy: 0 };
      dlg.style.transition = 'none';
    });
    addEventListener('pointermove', (e) => {
      if (!pull) return;
      pull.dy = Math.max(0, e.clientY - pull.y);
      dlg.style.translate = `0 ${pull.dy}px`;
    }, { passive: true });
    const drop = () => {
      if (!pull) return;
      const fast = pull.dy / Math.max(1, performance.now() - pull.t) > 0.6;
      const go = pull.dy > 110 || (fast && pull.dy > 30);
      pull = null;
      dlg.style.removeProperty('transition');
      if (!go) return dlg.style.removeProperty('translate');
      dlg.style.translate = '0 100%';
      setTimeout(() => {
        close();
        dlg.style.removeProperty('translate');
      }, reduce.matches ? 0 : 380);
    };
    addEventListener('pointerup', drop);
    addEventListener('pointercancel', drop);

    render();
    if (document.querySelector('[data-sr-open-bag]') && !isStatic()) open(); // the cart page
  }

  /* ---- Shopify's Customer Privacy API, loaded once for the cookie card and for analytics ---- */
  function withPrivacyApi(cb) {
    const S = window.Shopify;
    if (S && S.customerPrivacy) return cb(S.customerPrivacy);
    if (!S || typeof S.loadFeatures !== 'function') return; // no API: Shopify's own banner stays in charge
    S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
      if (!err && S.customerPrivacy) cb(S.customerPrivacy);
    });
  }

  /* ---- analytics: PostHog (EU cloud), the same project as werk.build, sent through werk.build's own proxy
     (werk.build/ingest). It only does what the visitor allowed in the cookie card: Analytics on → everything, with a
     cookie on .werk.build that werk.build shares, so one visitor stays one person across both sites; Analytics off →
     anonymous counts without cookie or storage (cookieless_mode 'on_reject'); no choice yet → nothing is captured.
     Outside consent regions Shopify reports analytics as allowed. ---- */
  const PH_TOKEN = 'phc_qnWZa3Wdz5pK9J4jaXwgSrQfqAzvrXudpXrp9pKF8d5Z';
  const PH_HOST = 'https://werk.build/ingest';
  function initAnalytics() {
    if (window.werkPostHog || (window.Shopify && Shopify.designMode)) return;
    window.werkPostHog = true;
    withPrivacyApi((api) => {
      let ph = null;
      const apply = () => {
        if (!ph) return;
        let allowed = false;
        let choice = '';
        try {
          allowed = !!api.analyticsProcessingAllowed();
          choice = (api.currentVisitorConsent() || {}).analytics || '';
        } catch (e) {
          /* no answer: stay pending */
        }
        const now = ph.get_explicit_consent_status();
        if (allowed && now !== 'granted') ph.opt_in_capturing({ captureEventName: false });
        else if (!allowed && choice === 'no' && now !== 'denied') ph.opt_out_capturing();
      };
      const section = (location.pathname.match(/^\/pages\/([^/?#]+)/) || [])[1] || null;
      const s = document.createElement('script');
      s.src = PH_HOST + '/static/array.js';
      s.async = true;
      s.crossOrigin = 'anonymous';
      s.onload = () => {
        const p = window.posthog;
        if (!p || typeof p.init !== 'function') return;
        p.init(PH_TOKEN, {
          api_host: PH_HOST,
          ui_host: 'https://eu.posthog.com',
          defaults: '2026-05-30',
          cookieless_mode: 'on_reject',
          person_profiles: 'identified_only',
          enable_heatmaps: true,
          capture_dead_clicks: true,
          session_recording: { maskAllInputs: true },
          disable_surveys: true,
          loaded: (inst) => {
            ph = inst;
            inst.register({ site: 'showroom', section });
            apply();
          },
        });
      };
      document.head.appendChild(s);
      // Shopify fires this after every setTrackingConsent (our card) – follow the new choice right away
      document.addEventListener('visitorConsentCollected', apply);
      document.addEventListener('click', (e) => {
        const back = e.target.closest && e.target.closest('.sr-back, [data-sr-back]');
        if (back && ph) ph.capture('back_to_code_clicked', { section, url: back.href || null });
      });
      document.addEventListener('shopify:cart:lines-update', () => ph && ph.capture('showroom_added_to_cart', { section }));
    });
  }

  /* ---- cookie banner: the showroom's own card in front of Shopify's Customer Privacy API. The API decides whether
     this visitor has to be asked (region + the store's privacy settings); the choice is stored by Shopify, so
     Shopify Analytics, pixels and marketing follow it. Shopify's own banner is hidden only once this has taken over.
     ?sr-consent=show forces the card (for testing outside the EU). ---- */
  function initConsent() {
    const box = document.querySelector('[data-sr-consent]');
    if (!box || box.dataset.srReady || (window.Shopify && Shopify.designMode)) return;
    box.dataset.srReady = '1';
    const force = /[?&]sr-consent=show\b/.test(location.search);
    withPrivacyApi((api) => {
      const call = (fn, fallback) => {
        try {
          return typeof api[fn] === 'function' ? api[fn]() : fallback;
        } catch (e) {
          return fallback;
        }
      };
      const consent = () => call('currentVisitorConsent', null) || {};
      const chosen = () => Object.values(consent()).some((v) => v === 'yes' || v === 'no');
      const needed = call('shouldShowBanner', false);
      if (!force && !needed && !chosen() && !call('saleOfDataRegion', false)) return;

      const icon = box.querySelector('[data-sr-consent-toggle]');
      const card = box.querySelector('.sr-consent_card');
      const ask = card.querySelector('[data-pane="ask"]');
      const form = card.querySelector('[data-pane="choose"]');
      const phone = matchMedia('(max-width: 749px)');
      const KEYS = ['analytics', 'marketing', 'preferences'];
      const ease = getComputedStyle(root).getPropertyValue('--sr-ease').trim() || 'ease';
      let open = false;

      root.classList.add('sr-consent-own');
      box.hidden = false;
      document.querySelectorAll('[data-sr-consent-open]').forEach((b) => (b.hidden = false));

      // phones: the button rides on top of the open card, or above the sticky bar
      const lift = () => {
        let px = 0;
        if (phone.matches) {
          const bar = document.querySelector('[data-sr-bar].is-on');
          if (open) px = card.offsetHeight + 4;
          else if (bar) px = bar.offsetHeight + 8;
        }
        box.style.setProperty('--sr-consent-lift', px + 'px');
      };
      // a section's own floating UI wins over the button (same list as the bar)
      const pad = 10;
      const check = () => {
        if (!box.isConnected) return onFrame.delete(check);
        lift();
        const b = icon.getBoundingClientRect();
        let away = false;
        if (!open)
          for (const el of document.querySelectorAll(FLOATING)) {
            if (el.closest('.sr')) continue;
            const r = el.getBoundingClientRect();
            const cs = getComputedStyle(el);
            if (r.width < 1 || cs.visibility === 'hidden' || +cs.opacity < 0.05) continue;
            if (r.bottom > b.top - pad && r.top < b.bottom + pad && r.right > b.left - pad && r.left < b.right + pad) {
              away = true;
              break;
            }
          }
        box.classList.toggle('is-away', away);
        icon.inert = away;
      };
      onFrame.add(check);

      const show = (pane) => {
        const choose = pane === 'choose';
        ask.hidden = choose;
        form.hidden = !choose;
        box.classList.toggle('is-choosing', choose);
        if (choose) {
          const c = consent();
          KEYS.forEach((k) => (form.elements[k].checked = c[k] === 'yes'));
        }
      };
      const openCard = (pane, focus) => {
        show(pane);
        open = true;
        box.classList.add('is-open');
        icon.setAttribute('aria-expanded', 'true');
        lift();
        // the panel takes the focus, not a button inside it (nothing is pre-selected)
        if (focus) requestAnimationFrame(() => card.focus({ preventScroll: true }));
        requestTick();
      };
      const closeCard = () => {
        const had = card.contains(document.activeElement);
        open = false;
        box.classList.remove('is-open');
        icon.setAttribute('aria-expanded', 'false');
        lift();
        if (had) icon.focus({ preventScroll: true });
        requestTick();
      };
      // ask → choose: the same card grows up and to the right out of its old size
      const grow = () => {
        const a = card.getBoundingClientRect();
        show('choose');
        const b = card.getBoundingClientRect();
        lift();
        form.classList.remove('is-entering');
        void form.offsetWidth;
        form.classList.add('is-entering');
        if (!reduce.matches)
          card.animate(
            [
              { clipPath: `inset(${Math.max(0, b.height - a.height)}px ${Math.max(0, b.width - a.width)}px 0 0 round 20px)` },
              { clipPath: 'inset(0 0 0 0 round 20px)' },
            ],
            { duration: 650, easing: ease }
          );
        card.focus({ preventScroll: true });
      };
      const save = (c) => {
        const v = { analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences, sale_of_data: !!c.marketing };
        try {
          api.setTrackingConsent(v, () => {});
        } catch (e) {
          /* the choice stays unset – Shopify treats that as “no” where consent is required */
        }
        closeCard();
      };

      icon.addEventListener('click', () => (open ? closeCard() : openCard('choose', true)));
      document.querySelectorAll('[data-sr-consent-open]').forEach((b) => b.addEventListener('click', () => openCard('choose', true)));
      card.querySelector('[data-sr-consent-choose]').addEventListener('click', grow);
      card.querySelectorAll('[data-sr-consent-all]').forEach((b) =>
        b.addEventListener('click', () => {
          const yes = b.dataset.srConsentAll === 'yes';
          save({ analytics: yes, marketing: yes, preferences: yes });
        })
      );
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        save(Object.fromEntries(KEYS.map((k) => [k, form.elements[k].checked])));
      });
      box.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && open) closeCard();
      });
      phone.addEventListener('change', lift);

      // first visit in a consent region: the card is out, without taking the focus from the page
      if (force || (needed && !chosen())) requestAnimationFrame(() => openCard('ask', false));
      requestTick();
    });
  }

  /* ---- back to werk.build on iPhone/iPad. Links with target="werk-site" load werk.build's own tab (Back to code lands
     on #code), but iOS Safari loads a named tab in the background and never switches to it. Only here, and only when
     this tab came from werk.build (referrer, remembered for the tab):
     - same tab (werk.build opens the store in its own tab on iOS): the link has to stay in this tab – it does by
       itself while the tab is still called "werk-site", otherwise it is sent here by hand;
     - own tab with an opener (older werk.build tabs): the link does its normal job, then this tab closes and Safari
       falls back to the opener. Safari only allows that while the tab has not been to other pages – if it refuses,
       nothing changes.
     Desktop and Android switch tabs on their own and are left alone. PostHog's back_to_code_clicked still fires: same
     click, the page leaves afterwards (flushed on pagehide). ---- */
  const ios = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && !/Android/i.test(navigator.userAgent));
  let fromWerk = false;
  try {
    if (/^https:\/\/([a-z0-9-]+\.)*werk\.build\//.test(document.referrer) && !/^https:\/\/showroom\./.test(document.referrer))
      sessionStorage.setItem('sr-from-werk', '1');
    fromWerk = sessionStorage.getItem('sr-from-werk') === '1';
  } catch (err) {
    /* no storage: links stay as they are */
  }
  if (ios && fromWerk)
    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[target="werk-site"]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      let opener = null;
      try {
        opener = window.opener;
      } catch (err) {
        /* no access: same as no opener */
      }
      if (!opener || opener.closed) {
        if (window.name === 'werk-site') return; // target is this very tab
        e.preventDefault();
        location.assign(a.href);
        return;
      }
      setTimeout(() => {
        try {
          opener.focus();
        } catch (err) {
          /* Safari ignores it, harmless */
        }
        window.close();
      }, 250);
    });

  function init(scope = document) {
    buttons036(scope);
    initBag();
    scope.querySelectorAll('[data-sr-words]').forEach(splitWords);
    scope.querySelectorAll('[data-sr-cover]').forEach(initCover);
    scope.querySelectorAll('[data-sr-clip]').forEach((v) => clipIO.observe(v));
    scope.querySelectorAll('[data-sr-reveal]:not(.is-in)').forEach((el) => {
      if (isStatic()) el.classList.add('is-in');
      else io.observe(el);
    });
    initBar();
    initConsent();
    initAnalytics();
    requestTick();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  // theme editor: a re-rendered section gets its motion back
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
